import React, { useState } from "react";
import { router } from "@inertiajs/react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  MessageCircle,
  Plus,
  User,
  Calendar,
  ChevronRight,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

interface Discussion {
  id: number;
  title: string;
  content: string;
  user: {
    id: number;
    name: string;
    email: string;
  };
  comments_count: number;
  created_at: string;
  updated_at: string;
}

interface Offer {
  id: number;
  name: string;
}

interface DiscussionsProps {
  offer: Offer;
  discussions: Discussion[];
  is_admin: boolean;
}

export default function Discussions({
  offer,
  discussions,
  is_admin,
}: DiscussionsProps) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreateDiscussion = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    router.post(
      `/offers/${offer.id}/offer_discussions`,
      {
        discussion: {
          title,
          content,
        },
      },
      {
        preserveState: false,
        preserveScroll: false,
        onSuccess: () => {
          setIsCreateDialogOpen(false);
          setTitle("");
          setContent("");
        },
        onFinish: () => {
          setIsSubmitting(false);
        },
      }
    );
  };


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Discussions</h2>
          <p className="text-sm text-gray-600 mt-1">
            Forum-style discussions for this offer
          </p>
        </div>
        {is_admin && (
          <Button
            onClick={() => {
              setTitle("");
              setContent("");
              setIsCreateDialogOpen(true);
            }}
            className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 cursor-pointer"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Topic
          </Button>
        )}
      </div>

      {/* Discussions List */}
      {discussions.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No discussions
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {is_admin
                  ? "Get started by creating a new discussion topic."
                  : "No discussion topics have been created yet."}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="py-0 overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Topic
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Author
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Comments
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {discussions.map((discussion) => (
                    <tr
                      key={discussion.id}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => router.visit(`/discussions/${discussion.id}?from=offer`)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                        >
                          {discussion.title}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{discussion.user.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <MessageCircle className="w-4 h-4 mr-1 text-gray-400" />
                          {discussion.comments_count}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(discussion.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Discussion Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Discussion Topic</DialogTitle>
            <DialogDescription>
              Start a new discussion topic for this offer.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateDiscussion}>
            <div className="space-y-4 my-4">
              <div>
                <label 
                  htmlFor="title"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter discussion title"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <label 
                  htmlFor="content"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  Content <span className="text-red-500">*</span>
                </label>
                <Textarea
                  id="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Enter discussion content"
                  required
                  rows={6}
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 cursor-pointer"
              >
                {isSubmitting ? "Creating..." : "Create Topic"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
